import { FC, ForwardedRef, forwardRef, memo, useMemo } from "react";
import { OptionsFilter } from "@mantine/core";
import { COUNTRIES_LIST } from "@/shared/consts";
import { TEntitySelectorProps, SearchableSelect } from "@/shared/ui/select";

interface CountriesSelectProps extends TEntitySelectorProps {
  value: string | null;
  onChange: (ISO: string | null) => void;
}

type TCountryComboboxItem = {
  label: string;
  value: string;
  alias: string[];
};

export const CountriesSelect: FC<CountriesSelectProps> = memo(
  forwardRef(
    (
      {
        value: selectedCountry = null,
        onChange: onSelect,
        placeholder = "Select country",
        error,
        width,
      },
      ref: ForwardedRef<HTMLDivElement>,
    ) => {
      const memoizedCountriesList = useMemo(() => {
        return COUNTRIES_LIST.map((country) => ({
          label: country.Country,
          value: country.ISO,
          alias: country.Alias,
        }));
      }, [COUNTRIES_LIST]);

      const optionsFilter: OptionsFilter = ({ options, search }) => {
        const searchWords = search
          .toLowerCase()
          .trim()
          .split(/\s+/)
          .filter(Boolean);

        return (options as TCountryComboboxItem[]).filter((option) => {
          if (searchWords.length === 0) {
            return true;
          }

          const aliases = option.alias.map((a) => a.toLowerCase());
          return searchWords.every((searchWord) =>
            aliases.some((alias) => alias.includes(searchWord)),
          );
        });
      };

      return (
        <div ref={ref}>
          <SearchableSelect
            width={width}
            size="md"
            data={memoizedCountriesList}
            placeholder={placeholder}
            value={selectedCountry}
            onChange={(value) => onSelect(value)}
            onClear={() => onSelect(null)}
            filter={optionsFilter}
            clearable
            error={error}
          />
        </div>
      );
    },
  ),
);
